import React, { useState, useMemo } from 'react';
import { 
  TrendingUp, DollarSign, Users, Target, BarChart3, 
  ArrowUp, ArrowDown, ChevronLeft, ChevronRight
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart as RechartPie, Pie, Cell, Legend
} from 'recharts';
import { formatCurrency } from '@/utils/helpers';
import useApiData from '@/hooks/useApiData';

const COLORS = ['#6C3483', '#2874A6', '#00C49F', '#F39C12', '#E74C3C', '#8B5CF6', '#EC4899'];

// ─── Helpers ───
function getMesAtual() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatMes(mes) {
  const [y, m] = mes.split('-');
  const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  return `${meses[parseInt(m) - 1]}/${y}`;
}

function navigateMes(mes, delta) {
  const [y, m] = mes.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// ─── Card de Métrica ───
const MetricCard = ({ icon: Icon, label, value, subValue, color = "primary", trend, trendUp, badge, onClick }) => (
  <div 
    className={`bg-white p-6 rounded-2xl border border-slate-100 shadow-sm transition-all duration-200 ${onClick ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between mb-4">
      <div className={`h-12 w-12 rounded-xl bg-${color}/10 flex items-center justify-center text-${color}`}>
        <Icon className="h-6 w-6" />
      </div>
      {badge && (
        <span className={`text-[10px] font-bold px-2 py-1 rounded-full bg-${badge.color}-100 text-${badge.color}-700 uppercase tracking-widest`}>
          {badge.label}
        </span>
      )}
    </div>
    <p className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-1">{label}</p>
    <p className="text-3xl font-bold text-slate-900">{value}</p>
    {subValue && <p className="text-sm text-slate-500 mt-1">{subValue}</p>}
    {trend && (
      <div className={`flex items-center gap-1 mt-2 text-sm ${trendUp ? 'text-emerald-600' : 'text-red-500'}`}>
        {trendUp ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
        <span className="font-semibold">{trend}</span>
      </div>
    )}
  </div>
);

// ─── Página Principal ───
export default function Metricas() {
  const [mesFiltro, setMesFiltro] = useState(getMesAtual());

  // Dados Globais
  const { data: metricas, isLoading: loadingMetricas } = useApiData('/advanced/metricas');
  const { data: custosFixosData, isLoading: loadingCustos } = useApiData('/advanced/custos-fixos');
  
  // Como as métricas dependem do mesFiltro nos gráficos de detalhe, buscamos investimentos do mesFiltro para poder extrair
  const { data: investimentosData } = useApiData(`/advanced/investimentos?mes=${mesFiltro}`);

  const custosFixos = Array.isArray(custosFixosData) ? custosFixosData : [];
  // eslint-disable-next-line
  const investimentos = Array.isArray(investimentosData) ? investimentosData : [];

  // Cálculos do mês atual (para o geral)
  const totalInvestido = investimentos.reduce((s, i) => s + (i.valor || 0), 0);
  const totalRetorno = investimentos.reduce((s, i) => s + (i.retorno_obtido || 0), 0);
  const receita = metricas?.receita_total || 0;

  // ROI Geral / Médio
  const roiReal = useMemo(() => {
    if (totalInvestido === 0) return null;
    const retornoFinal = totalRetorno > 0 ? totalRetorno : receita; // usa retorno_obtido ou receita total
    return ((retornoFinal - totalInvestido) / totalInvestido) * 100;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalInvestido, totalRetorno, receita]);

  // Chart data investimentos por canal
  const invPorCanal = useMemo(() => {
    const acc = {};
    investimentos.forEach(inv => {
      acc[inv.canal] = (acc[inv.canal] || 0) + inv.valor;
    });
    return Object.entries(acc).map(([name, value]) => ({ name, value }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investimentosData]);

  // Chart custos
  const custosPorCategoria = custosFixos.reduce((acc, c) => {
    const cat = c.categoria || 'Outros';
    acc[cat] = (acc[cat] || 0) + c.valor_mensal;
    return acc;
  }, {});
  const custosChartData = Object.entries(custosPorCategoria).map(([name, value]) => ({ name, value }));
  
  const margemData = [
    { name: 'Receita', value: metricas?.receita_total || 0 },
    { name: 'Custo Operac.', value: metricas?.custo_total_operacional || 0 },
    { name: 'Investimento', value: totalInvestido },
    { name: 'Lucro Líq.', value: metricas?.lucro_liquido || 0 },
  ];

  if (loadingMetricas || loadingCustos) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4" />
          <p className="text-slate-600">Carregando métricas gerais...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="metricas-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight text-slate-900">Métricas Financeiras</h1>
          <p className="text-slate-600 mt-1">Visão geral executiva da saúde financeira da agência</p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl px-2 py-1">
            <button onClick={() => setMesFiltro(m => navigateMes(m, -1))} className="p-1 hover:text-violet-600 rounded">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-bold text-slate-700 px-2 min-w-[80px] text-center">{formatMes(mesFiltro)}</span>
            <button onClick={() => setMesFiltro(m => navigateMes(m, 1))} className="p-1 hover:text-violet-600 rounded">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
      </div>

      {/* KPIs Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          icon={DollarSign}
          label="MRR Atual"
          value={formatCurrency(metricas?.mrr_atual || 0)}
          subValue="Receita Mensal Recorrente"
          color="primary"
        />
        <MetricCard
          icon={TrendingUp}
          label="Margem Líquida"
          value={`${(metricas?.margem_liquida_percentual || 0).toFixed(1)}%`}
          subValue={formatCurrency(metricas?.lucro_liquido || 0)}
          color="accent"
          trend={metricas?.margem_liquida_percentual > 20 ? "Saudável" : "Atenção"}
          trendUp={metricas?.margem_liquida_percentual > 20}
        />

        {/* Simplificação Card ROI Geral */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${
              roiReal === null ? 'bg-slate-100 text-slate-400' :
              roiReal >= 100 ? 'bg-emerald-100 text-emerald-600' :
              roiReal >= 0 ? 'bg-blue-100 text-blue-600' : 'bg-red-100 text-red-600'
            }`}>
              <Target className="h-6 w-6" />
            </div>
            {roiReal !== null && (
              <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-widest ${
                roiReal >= 100 ? 'bg-emerald-100 text-emerald-700' :
                roiReal >= 0 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
              }`}>
                {roiReal >= 100 ? 'Excelente' : roiReal >= 0 ? 'Positivo' : 'Negativo'}
              </span>
            )}
          </div>
          <p className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-1">ROI Mensal (Geral)</p>
          <p className={`text-3xl font-bold ${
            roiReal === null ? 'text-slate-400' : roiReal >= 0 ? 'text-slate-900' : 'text-red-600'
          }`}>
            {roiReal !== null ? `${roiReal.toFixed(1)}%` : '--'}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            Total Inv: {formatCurrency(totalInvestido)}
          </p>
        </div>

        <MetricCard
          icon={Users}
          label="Clientes Ativos"
          value={metricas?.numero_clientes_ativos || 0}
          subValue={`Ticket médio: ${formatCurrency(metricas?.lucro_medio_por_cliente || 0)}`}
          color="info"
        />
      </div>

      {/* Resumo Financeiro (Cards em Grid) */}
      <div className="bg-gradient-to-r from-primary to-secondary p-8 rounded-2xl text-white">
        <h3 className="text-xl font-heading font-semibold mb-6">Resumo Operacional</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Receita Total</p>
            <p className="text-2xl font-bold">{formatCurrency(metricas?.receita_total || 0)}</p>
          </div>
          <div>
            <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Custo Operacional</p>
            <p className="text-2xl font-bold">{formatCurrency(metricas?.custo_total_operacional || 0)}</p>
          </div>
          <div>
            <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Total Investido (Mês)</p>
            <p className="text-2xl font-bold">{formatCurrency(totalInvestido)}</p>
          </div>
          <div>
            <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Lucro Líquido</p>
            <p className="text-2xl font-bold">{formatCurrency(metricas?.lucro_liquido || 0)}</p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
          <h3 className="text-base font-bold text-slate-900 mb-4">Estrutura de Custos</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600 text-sm">Custo Fixo Mensal</span>
              <span className="font-bold text-slate-900">{formatCurrency(metricas?.custo_fixo_mensal || 0)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600 text-sm">Custo Variável</span>
              <span className="font-bold text-slate-900">{formatCurrency(metricas?.custo_variavel_mensal || 0)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-violet-50 rounded-lg border border-violet-100">
              <span className="text-violet-700 font-medium text-sm">Investimentos ({formatMes(mesFiltro)})</span>
              <span className="font-bold text-violet-700">{formatCurrency(totalInvestido)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg border border-primary/20">
              <span className="text-primary font-medium text-sm">Custo Total Operacional</span>
              <span className="font-bold text-primary">{formatCurrency(metricas?.custo_total_operacional || 0)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-accent/10 rounded-lg border border-accent/20">
              <span className="text-accent font-medium text-sm">Margem de Contribuição</span>
              <span className="font-bold text-accent">{formatCurrency(metricas?.margem_contribuicao || 0)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
          <h3 className="text-base font-bold text-slate-900 mb-4">
            {invPorCanal.length > 0 ? 'Investimento por Canal' : 'Custos por Categoria'}
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <RechartPie>
              <Pie
                data={invPorCanal.length > 0 ? invPorCanal : custosChartData}
                cx="50%" cy="50%"
                innerRadius={60} outerRadius={80}
                paddingAngle={5} dataKey="value"
              >
                {(invPorCanal.length > 0 ? invPorCanal : custosChartData).map((_, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Legend />
            </RechartPie>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
          <h3 className="text-base font-bold text-slate-900 mb-4">Receita vs Custo vs Lucro</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={margemData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" stroke="#64748b" tick={{ fontSize: 10 }} />
              <YAxis stroke="#64748b" />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {margemData.map((entry, i) => (
                  <Cell key={i} fill={i === 3 ? '#10b981' : i === 2 ? '#8B5CF6' : '#6C3483'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Análise por cliente */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
          <h3 className="text-base font-bold text-slate-900 mb-4">Análise por Cliente</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg">
              <div>
                <p className="text-sm text-slate-500">Lucro Médio por Cliente</p>
                <p className="text-2xl font-bold text-primary">{formatCurrency(metricas?.lucro_medio_por_cliente || 0)}</p>
              </div>
              <TrendingUp className="h-10 w-10 text-primary/50" />
            </div>
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-violet-50 to-violet-100/50 rounded-lg">
              <div>
                <p className="text-sm text-slate-500">Custo Médio por Cliente</p>
                <p className="text-2xl font-bold text-violet-700">{formatCurrency(metricas?.custo_medio_por_cliente || 0)}</p>
              </div>
              <BarChart3 className="h-10 w-10 text-violet-400" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
          <h3 className="text-base font-bold text-slate-900 mb-4">Top Clientes mais Lucrativos</h3>
          {metricas?.clientes_mais_lucrativos?.length > 0 ? (
            <div className="space-y-3">
              {metricas.clientes_mais_lucrativos.map((cliente, index) => (
                <div key={cliente.client_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      index === 0 ? 'bg-yellow-100 text-yellow-700' :
                      index === 1 ? 'bg-slate-200 text-slate-700' :
                      index === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-slate-100 text-slate-500'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="font-medium text-slate-900 text-sm">{cliente.client_name}</span>
                  </div>
                  <span className="font-bold text-emerald-600">{formatCurrency(cliente.receita)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-[200px] text-slate-400">Nenhum dado disponível</div>
          )}
        </div>
      </div>
    </div>
  );
}
